import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { AdflowLogo } from '@adflow/frontend/ui-assets';
import { su, theme } from '@adflow/frontend/ui-theme';
import { createErrorToast } from '@adflow/frontend/ui-toasts';
import {
	useAuthSafe,
	useRedirectFromLogin,
	useSsoCapabilityQuery,
} from '@adflow/frontend/util-auth';

import { LoginForm } from './LoginForm';
import { MicrosoftLoginButton } from './MicrosoftLoginButton';

function VersionDisplay() {
	const buildDate = document.lastModified;
	const appTag =
		process.env.NX_PUBLIC_RELEASE_VERSION ??
		process.env.NX_PUBLIC_ENVIRONMENT ??
		'development';
	const appVersion = process.env.NX_PUBLIC_APP_VERSION ?? 'local';

	return (
		<div
			css={[
				su.textXs,
				{
					position: 'fixed',
					display: 'flex',
					gap: '0.25rem',
					right: '0.5rem',
					bottom: '0.5rem',

					color: theme.colors.action.disabledBackground,
					'&:hover': { color: theme.colors.text.primary },
				},
			]}
		>
			{appTag ? <div>{appTag}</div> : null}
			{appVersion ? <div>({appVersion.slice(0, 7)})</div> : null}
			<div>{buildDate}</div>
		</div>
	);
}

function useRedirectIfAlreadyLoggedIn() {
	const { user, status } = useAuthSafe();

	const redirectFromLogin = useRedirectFromLogin(user);

	useEffect(() => {
		if (user && status === 'logged-in') {
			setTimeout(() => redirectFromLogin(), 60);
		}
	}, [user, status, redirectFromLogin]);
}

function useShowSsoErrorToast() {
	const { t } = useTranslation('login');
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		const ssoError = searchParams.get('ssoError');
		if (!ssoError) return;

		createErrorToast(t([`sso error ${ssoError}`, 'sso error unknown']));
		setSearchParams(
			(prev) => {
				prev.delete('ssoError');
				return prev;
			},
			{ replace: true }
		);
	}, [searchParams, setSearchParams, t]);
}

const LOGO_SIZE = 200;

export default function LoginPage() {
	const { t } = useTranslation(['common', 'login']);

	useRedirectIfAlreadyLoggedIn();
	useShowSsoErrorToast();

	const { data: ssoCapability, isPending: ssoCapabilityIsPending } =
		useSsoCapabilityQuery();

	useEffect(() => {
		document.title = `${t('appName')}: ${t('login:login')}`;
	}, [t]);

	return (
		<div
			css={
				// Take up full screen and center content
				{
					alignItems: 'center',
					justifyContent: 'center',
					width: '100vw',
					height: '100vh',
					display: 'grid',
					gridTemplateRows: 'repeat(1, minmax(0, 1fr))',

					gridTemplateColumns: `${LOGO_SIZE}px 13rem`,
					columnGap: '8rem',

					backgroundImage: `linear-gradient(#ffffff, ${theme.colors.background.loginScreen})`,
				}
			}
		>
			<h1 css={su.visuallyHidden}>{t('appName')}</h1>
			<AdflowLogo width={LOGO_SIZE} height={LOGO_SIZE} title="AdFlow" />
			{ssoCapabilityIsPending ? null : ssoCapability?.microsoftEnabled ? (
				<MicrosoftLoginButton />
			) : (
				<LoginForm />
			)}
			<VersionDisplay />
		</div>
	);
}
